import React, { useMemo } from 'react';
import { Alert, Box } from '@mui/material';
import {
    CTableWrapper,
    DBlock,
    DBlockHeader,
    ICustomTableWrapperProps,
} from '../../../../dum';
import { formatFloat } from '../../../../../helpers';
import { MaterialBalanceMachinesQuery } from '../../../../../services';

type MachineRecord =
    MaterialBalanceMachinesQuery['getMaterialBalanceByMachine'][number];

/**
 * Which machine's consumption capture is off, for the period the user drilled
 * into. This is the step-5 drill-down; it is not a standalone view.
 */
export function MaterialBalanceMachines({
    records,
    periodLabel,
    loading,
}: {
    records: MachineRecord[];
    periodLabel: string;
    loading?: boolean;
}) {
    // Typed to the row type, not to `ICustomTableWrapperItem` (which is fixed to
    // `Record<any, any>`): `content` is a function property, so under
    // strictFunctionTypes its parameter is checked contravariantly, and
    // `Record<any, any>` is NOT assignable to a specific record type. Annotating
    // the parameters against the `Record` alias fails; naming the row type here
    // both compiles and keeps the field names checked.
    const items = useMemo<
        ICustomTableWrapperProps<MachineRecord>['items']
    >(() => {
        return [
            {
                title: 'Máquina',
                textAlign: 'left',
                content: (item) => item.machine_name || '—',
            },
            {
                title: 'Bobina consumida',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_consumed, 0),
            },
            {
                title: 'Bolsa producida',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_produced, 0),
            },
            {
                title: 'Diferencia kg (sin merma)',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_difference, 0),
            },
            {
                title: 'Diferencia % (sin merma)',
                textAlign: 'right',
                content: (item) =>
                    item.difference_percentage === null ||
                    item.difference_percentage === undefined
                        ? 'sin consumo'
                        : `${formatFloat(item.difference_percentage)} %`,
            },
        ];
    }, []);

    return (
        <DBlock>
            <DBlockHeader subtitle={`Por máquina — ${periodLabel}`} />
            <Alert severity={'info'} sx={{ mb: 2 }}>
                Esta tabla <strong>no incluye la merma</strong>: la merma se
                registra por corrida, no por máquina, y repartirla entre máquinas
                inventaría precisión que nunca se midió. Por eso todas las
                diferencias aquí salen positivas — aproximadamente el porcentaje
                de merma. Compare las máquinas <strong>entre sí</strong>, no
                contra cero.
            </Alert>
            <Box>
                <CTableWrapper
                    items={items}
                    data={records}
                    loading={loading}
                />
            </Box>
        </DBlock>
    );
}
